import { createAction, props } from '@ngrx/store';
import { RefreshCellsParams } from 'ag-grid-community';

import { SearchFormType } from '../search-page-type.enum';
import { SearchTypeInfo } from '../search-param.model';
import { SearchPageDiff } from './search-page.state';
import { ISourceItemData, Burner } from '@se/models';

export enum SearchPageActions {
    OPEN = '[SearchPage] Open',
    SEARCH_REFRESH = '[SearchPage] SearchRefresh',
    SAFE_EXIT = '[SearchPage] SafeExit',
    SEARCH = '[SearchPage] Search',
    SEARCH_IF_FILTERS_EQ = '[SearchPage] SearchIfFiltersEq',
    SEARCH_TYPE_CHANGE = '[SearchPage] SearchTypeChange',
    SELECT = '[SearchPage] Select',
    SET_DIFF = '[SearchPage] SetDiff',
    SET_ITEMS = '[SearchPage] SetItems',
    TOGGLE_LEFT_PANEL = '[SearchPage] ToggleLeftPanel',
    TOGGLE_RIGHT_PANEL = '[SearchPage] ToggleRightPanel',
    TOGGLE_TIMELINE = '[SearchPage] ToggleTimeline',
    UNLOCK_READONLY = '[SearchPage] UnlockReadonly',
    UPDATE_ITEMS_PROP = '[SearchPage] UpdateItemsProp',
    UPDATE_ITEMS = '[SearchPage] UpdateItems',
    REMOVE_ITEMS = '[SearchPage] RemoveItems',
    SEARCH_COMPLETED = '[SearchPage] SearchCompleted',
    UPDATE_CELLS = '[SearchPage] UpdateCells',
    SELECT_EQUIPMENT = '[SearchPage] SelectEquipment',
    WORKLIST_TYPE = '[SearchPage] SeWorklistType',
    SELECT_BURN_DEVICE = '[SearchPage] SeSelectBurnDevice',
}

/**
 * Azione scatenata al cambio di stato dello slider sinistro.
 */
export const seSearchToggleLeftArea = createAction(
    SearchPageActions.TOGGLE_LEFT_PANEL,
    props<{
        formType: SearchFormType;
        hide: boolean;
    }>(),
);

/**
 * Azione scatenata al cambio di stato dello slider destro.
 */
export const seSearchToggleRightArea = createAction(
    SearchPageActions.TOGGLE_RIGHT_PANEL,
    props<{
        formType: SearchFormType;
        hide: boolean;
    }>(),
);

/**
 * Azione scatenata al cambio di stato della timeline.
 */
export const seSearchToggleTimeline = createAction(
    SearchPageActions.TOGGLE_TIMELINE,
    props<{
        formType: SearchFormType;
        hide: boolean;
    }>(),
);

/**
 * Azione scatenata al doppio click su un item della griglia.
 */
export const seSearchOpen = createAction(SearchPageActions.OPEN, props<{ rows: any[] }>());

/**
 * Azione utilizzata per chiamare il "refreshCells" di ag-grid.
 */
export const seSearchUpdateCells = createAction(
    SearchPageActions.UPDATE_CELLS,
    props<{
        params: RefreshCellsParams;
    }>(),
);

/**
 * Pulizia completa dello stato.
 */
export const seSearchSafeExit = createAction(SearchPageActions.SAFE_EXIT, props<{ formType: SearchFormType }>());

/**
 * Azione sollevata al termine della search su server (riporta la vecchia selezione che la setItems perde)
 */
export const seSearchCompleted = createAction(
    SearchPageActions.SEARCH_COMPLETED,
    props<{
        formType: SearchFormType;
        addedRows: any[];
        oldSelectedItems: any[];
        highlightRow: boolean;
    }>(),
);

/**
 * Azione di ricerca.
 */
export const seSearch = createAction(
    SearchPageActions.SEARCH,
    props<{
        formType: SearchFormType;
        searchType?: SearchTypeInfo;
        highlightRow?: boolean;
        showDiff?: boolean;
    }>(),
);

/**
 * Azione di ricerca filtrabile.
 */
export const seSearchIfFiltersEq = createAction(
    SearchPageActions.SEARCH_IF_FILTERS_EQ,
    props<{
        formType: SearchFormType;
        searchType?: SearchTypeInfo;
        highlightRow?: boolean;
        showDiff?: boolean;
        filters: Record<string, any>;
    }>(),
);

/**
 * Azione per la modifica del tipo di ricerca in una pagina.
 */
export const seSearchTypeChange = createAction(
    SearchPageActions.SEARCH_TYPE_CHANGE,
    props<{
        formType: SearchFormType;
        searchType?: SearchTypeInfo;
    }>(),
);

/**
 * Azione per la selezione di uno o più item in griglia.
 */
export const seSearchSelect = createAction(
    SearchPageActions.SELECT,
    props<{
        formType: SearchFormType;
        items: string[];
        rows: any[];
    }>(),
);

/**
 * Azione per impostare il diff degli item in griglia.
 */
export const seSearchSetDiff = createAction(
    SearchPageActions.SET_DIFF,
    props<{
        formType: SearchFormType;
        diff?: SearchPageDiff[];
    }>(),
);

/**
 * Azione per impostare gli item in griglia.
 */
export const seSearchSetItems = createAction(
    SearchPageActions.SET_ITEMS,
    props<{
        formType: SearchFormType;
        items: any[];
    }>(),
);

/**
 * Azione lanciata al click sul lucchetto di un item.
 */
export const seSearchUnlockReadonly = createAction(
    SearchPageActions.UNLOCK_READONLY,
    props<{
        formType: SearchFormType;
        items: any[];
    }>(),
);

/**
 * Azione per la modifica inline di uno o più item in griglia.
 */
export const seSearchUpdateItemsProps = createAction(
    SearchPageActions.UPDATE_ITEMS_PROP,
    props<{
        formType: SearchFormType;
        items: string[];
        prop: string;
        value: any;
    }>(),
);

/**
 * Azione per la modifica inline di uno o più item in griglia. (cerca l'id e se presente sostituiscel'intera riga)
 */
export const seSearchUpdateItems = createAction(
    SearchPageActions.UPDATE_ITEMS,
    props<{
        formType: SearchFormType;
        items: any[];
        idPropertyName: string;
        highlightRow: boolean;
    }>(),
);

/**
 * Azione per rimuovere elementi tra gli items della griglia (cerca l'id e rimuove la riga senza rifare la search)
 */
export const seSearchRemoveItems = createAction(
    SearchPageActions.REMOVE_ITEMS,
    props<{
        formType: SearchFormType;
        idList: any[];
        idPropertyName: string;
    }>(),
);

/**
 * Azione per salvare la macchina selezionata dall'utente nello store della worklist
 */
export const seSetSelectedEquipment = createAction(
    SearchPageActions.SELECT_EQUIPMENT,
    props<{
        formType: SearchFormType;
        equipment: ISourceItemData;
    }>(),
);

/**
 * Azione recupero WL solo la prima volta che entro in WL.
 */
export const seWorklistType = createAction(
    SearchPageActions.WORKLIST_TYPE,
    props<{
        formType: SearchFormType;
    }>(),
);

/**
 * Azione per salvare il masterizzatore selezionato dall'utente
 */
export const seSetBurnDevice = createAction(
    SearchPageActions.SELECT_BURN_DEVICE,
    props<{
        formType: SearchFormType;
        device: Burner | null;
    }>(),
);
